import { useLockScroll } from "@/hooks/useLockScroll";
import useUnmountedRef from "@/hooks/useUnmountedRef";
import {
	NativeProps,
	ShouldRender,
	renderToContainer,
	withNativeProps,
	withStopPropagation,
} from "@/utils/components-utils";
import { animated, useIsomorphicLayoutEffect, useSpring } from "@react-spring/web";
import clsx from "clsx";
import { MouseEventHandler, useRef, useState } from "react";

interface OverlayProps extends NativeProps {
	visible?: boolean;
	onClose: (e: MouseEventHandler<HTMLDivElement>) => void;
	children?: React.ReactNode;
	zIndex?: number;
	bgLevel?: "normal" | "deep";
}

export default function Overlay(props: OverlayProps) {
	const [active, setActive] = useState(props.visible);
	useIsomorphicLayoutEffect(() => {
		if (props.visible) {
			setActive(true);
		} else {
			setActive(false);
		}
	}, [props.visible]);

	const ref = useRef<HTMLDivElement>(null);
	useLockScroll(ref, active ? "strict" : false);

	const unmountedRef = useUnmountedRef();
	const { opacity } = useSpring({
		opacity: props.visible ? 1 : 0,
		config: {
			precision: 0.01,
			mass: 1,
			tension: 250,
			friction: 30,
			clamp: true,
		},
		onRest: () => {
			if (unmountedRef.current) return;
			setActive(props.visible);
		},
	});

	const node = withStopPropagation(
		["click"],
		withNativeProps(
			props,
			<animated.div
				className={clsx(
					"fixed left-0 top-0 w-full h-full",
					props.bgLevel === "deep" ? "bg-white/[70%] dark:bg-black/[70%]" : "bg-white/[34%] dark:bg-black/[34%]",
				)}
				style={{
					opacity,
					zIndex: props.zIndex || 1,
					display: active ? undefined : "none",
				}}
				ref={ref}
				onClick={(e) => {
					e.stopPropagation();
					if (e.target === e.currentTarget) {
						props.onClose?.(e as any);
					}
				}}
			>
				{props.children}
			</animated.div>,
		),
	);
	return (
		<ShouldRender active={active || false} forceRender={true} destroyOnClose={true}>
			{renderToContainer(() => document.body, node)}
		</ShouldRender>
	);
}
